import { useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useDesignMode } from "@/hooks/useDesignMode";
import { isEditorUIElement } from "./designUtils";

const routeToPage: Record<string, string> = {
  "/": "home",
  "/auth": "auth",
  "/subscription": "subscription",
  "/pro/dashboard": "dashboard",
  "/search": "search",
  "/messages": "messages",
};

interface OverlayData {
  id: string;
  page: string;
  image_url: string;
  position_x: number;
  position_y: number;
  width: number;
  height: number;
  rotation: number;
  opacity: number;
  z_index: number;
  is_active: boolean;
  label: string;
  position_type: string;
}

const DesignModeOverlays = () => {
  const {
    isDesignMode,
    selectedElementId,
    selectElement,
    elements,
    setElements,
    updateElement,
  } = useDesignMode();

  const location = useLocation();
  const [loaded, setLoaded] = useState(false);
  const dragRef = useRef<{
    id: string;
    startX: number;
    startY: number;
    startPosX: number;
    startPosY: number;
    action: "move" | "resize" | "rotate";
    startWidth?: number;
    startHeight?: number;
    startRotation?: number;
    centerX?: number;
    centerY?: number;
  } | null>(null);

  // Load overlays into design elements — re-fetch on route change so admin edits take effect
  useEffect(() => {
    const loadOverlays = async () => {
      const { data } = await supabase
        .from("page_overlays" as any)
        .select("*")
        .order("z_index", { ascending: true });
      if (data) {
        const designElements = (data as any as OverlayData[]).map((o) => ({
          id: o.id,
          type: "overlay" as const,
          position_x: o.position_x,
          position_y: o.position_y,
          width: o.width,
          height: o.height,
          rotation: o.rotation,
          opacity: o.opacity,
          z_index: o.z_index,
          image_url: o.image_url,
          page: o.page,
          label: o.label,
          is_active: o.is_active,
          positionType: (o.position_type === "absolute" ? "absolute" : o.position_type === "sticky" ? "sticky" : "fixed") as "fixed" | "absolute" | "sticky",
        }));
        setElements(designElements);
      }
      setLoaded(true);
    };
    loadOverlays();
  }, [setElements, location.pathname]);

  const currentPage = routeToPage[location.pathname] || "";

  const visibleElements = elements.filter(
    (el) =>
      el.type === "overlay" &&
      !el.isHidden &&
      (el.is_active || isDesignMode) &&
      (el.page === "all" || el.page === currentPage)
  );

  const handleMouseDown = useCallback(
    (
      e: React.MouseEvent,
      id: string,
      action: "move" | "resize" | "rotate"
    ) => {
      if (!isDesignMode) return;
      const el = elements.find((el) => el.id === id);
      if (!el || el.isLocked) return;
      e.preventDefault();
      e.stopPropagation();
      selectElement(id);


      if (action === "rotate") {
        const rect = (e.currentTarget as HTMLElement).closest("[data-overlay-id]")?.getBoundingClientRect();
        dragRef.current = {
          id,
          startX: e.clientX,
          startY: e.clientY,
          startPosX: el.position_x,
          startPosY: el.position_y,
          action: "rotate",
          startRotation: el.rotation,
          centerX: rect ? rect.left + rect.width / 2 : e.clientX,
          centerY: rect ? rect.top + rect.height / 2 : e.clientY,
        };
      } else if (action === "resize") {
        dragRef.current = {
          id,
          startX: e.clientX,
          startY: e.clientY,
          startPosX: el.position_x,
          startPosY: el.position_y,
          action: "resize",
          startWidth: el.width,
          startHeight: el.height,
        };
      } else {
        dragRef.current = {
          id,
          startX: e.clientX,
          startY: e.clientY,
          startPosX: el.position_x,
          startPosY: el.position_y,
          action: "move",
        };
      }
    },
    [isDesignMode, elements, selectElement]
  );

  useEffect(() => {
    if (!isDesignMode) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!dragRef.current) return;
      const { id, action, startX, startY } = dragRef.current;

      if (action === "move") {
        const dx = ((e.clientX - startX) / window.innerWidth) * 100;
        const dy = ((e.clientY - startY) / window.innerHeight) * 100;
        updateElement(id, {
          position_x: Math.max(0, Math.min(100, dragRef.current.startPosX + dx)),
          position_y: Math.max(0, Math.min(100, dragRef.current.startPosY + dy)),
        });
      } else if (action === "resize") {
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        updateElement(id, {
          width: Math.max(20, (dragRef.current.startWidth || 100) + dx),
          height: Math.max(20, (dragRef.current.startHeight || 100) + dy),
        });
      } else if (action === "rotate") {
        const cx = dragRef.current.centerX || 0;
        const cy = dragRef.current.centerY || 0;
        const startAngle = Math.atan2(startY - cy, startX - cx);
        const currentAngle = Math.atan2(e.clientY - cy, e.clientX - cx);
        const angleDiff = ((currentAngle - startAngle) * 180) / Math.PI;
        updateElement(id, {
          rotation: Math.round((dragRef.current.startRotation || 0) + angleDiff),
        });
      }
    };

    const handleMouseUp = () => {
      dragRef.current = null;
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDesignMode, updateElement]);

  // Click outside to deselect — ignore all editor UI and Radix portals
  useEffect(() => {
    if (!isDesignMode) return;
    const handler = (e: Event) => {
      const target = e.target as HTMLElement;
      if (isEditorUIElement(target)) return;
      selectElement(null);
    };
    // Listen on both click and pointerdown to catch Radix interactions
    window.addEventListener("click", handler);
    window.addEventListener("pointerdown", handler);
    return () => {
      window.removeEventListener("click", handler);
      window.removeEventListener("pointerdown", handler);
    };
  }, [isDesignMode, selectElement]);

  if (!loaded) return null;

  return (
    <>
      {visibleElements.map((el) => {
        const isSelected = isDesignMode && selectedElementId === el.id;
        return (
          <div
            key={el.id}
            data-overlay-id={el.id}
            className={`select-none ${
              isDesignMode && !el.isLocked
                ? "cursor-move"
                : isDesignMode && el.isLocked
                ? "cursor-not-allowed"
                : "pointer-events-none"
            }`}
            style={{
              position: (el.positionType || "fixed") as any,
              left: `${el.position_x}%`,
              top: `${el.position_y}%`,
              width: `${el.width}px`,
              height: `${el.height}px`,
              transform: `translate(-50%, -50%) rotate(${el.rotation}deg)`,
              opacity: el.opacity / 100,
              zIndex: el.z_index,
            }}
            onMouseDown={(e) => handleMouseDown(e, el.id, "move")}
            onClick={(e) => {
              if (isDesignMode) {
                e.stopPropagation();
                selectElement(el.id);
              }
            }}
          >
            <img
              src={el.image_url}
              alt={el.label || ""}
              className="w-full h-full object-contain pointer-events-none"
              draggable={false}
            />

            {/* Selection handles */}
            {isSelected && (
              <>
                {/* Border */}
                <div className="absolute inset-0 border-2 border-primary rounded pointer-events-none" />

                {/* Corner resize handles */}
                {["top-left", "top-right", "bottom-left", "bottom-right"].map(
                  (corner) => (
                    <div
                      key={corner}
                      className={`absolute w-3 h-3 bg-primary rounded-full border-2 border-white shadow cursor-se-resize ${
                        corner === "top-left"
                          ? "-top-1.5 -left-1.5"
                          : corner === "top-right"
                          ? "-top-1.5 -right-1.5"
                          : corner === "bottom-left"
                          ? "-bottom-1.5 -left-1.5"
                          : "-bottom-1.5 -right-1.5"
                      }`}
                      onMouseDown={(e) => handleMouseDown(e, el.id, "resize")}
                    />
                  )
                )}

                {/* Rotation handle */}
                <div
                  className="absolute -top-8 left-1/2 -translate-x-1/2 w-5 h-5 bg-primary rounded-full border-2 border-white shadow cursor-grab flex items-center justify-center"
                  onMouseDown={(e) => handleMouseDown(e, el.id, "rotate")}
                >
                  <svg viewBox="0 0 16 16" className="w-3 h-3 text-white fill-current">
                    <path d="M8 1a7 7 0 0 1 7 7h-2A5 5 0 0 0 8 3V1zm0 14a7 7 0 0 1-7-7h2a5 5 0 0 0 5 5v2z" />
                  </svg>
                </div>
                <div className="absolute -top-5 left-1/2 w-px h-3 bg-primary -translate-x-1/2" />

                {/* Label */}
                <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap shadow">
                  {el.label || "Image"}
                </div>
              </>
            )}
          </div>
        );
      })}
    </>
  );
};

export default DesignModeOverlays;
